import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { socialImageMeta } from "@/lib/og";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { PenSquare, ShieldCheck } from "lucide-react";
import { listApprovedReviews, getReviewStats } from "@/lib/reviews.functions";
import { ReviewCard } from "@/components/site/ReviewCard";
import { StarRating } from "@/components/site/StarRating";
import { RouteErrorState } from "@/components/site/RouteErrorState";

export const listOpts = queryOptions({
  queryKey: ["reviews", "public", "all"],
  queryFn: () => listApprovedReviews({ data: { limit: 200 } }),
  staleTime: 60_000,
});

export const statsOpts = queryOptions({
  queryKey: ["reviews", "stats", "all"],
  queryFn: () => getReviewStats({ data: {} }),
  staleTime: 60_000,
});

export const Route = createFileRoute("/omdomen")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(listOpts),
      context.queryClient.ensureQueryData(statsOpts),
    ]);
  },
  head: () => ({
    meta: [
      { title: "Omdömesöversikt — Skolvyn" },
      { name: "description", content: "Läs vad vårdnadshavare, elever och personal säger om Sveriges skolor. Omdömen kontrolleras automatiskt och kan rapporteras för manuell bedömning." },
      { property: "og:title", content: "Omdömesöversikt — Skolvyn" },
      { property: "og:description", content: "Öppen, modererad och trovärdig återkoppling om Sveriges skolor." },
    
        ...socialImageMeta({ path: "/omdomen" }),
      ],
  }),
  errorComponent: ({ error }) => (
    <RouteErrorState
      title="Kunde inte hämta omdömen"
      description="Vi når inte omdömesdatabasen just nu. Försök igen om en stund — under tiden kan du utforska skolor i registret."
      error={error}
      fallbackActions={[
        { to: "/skolor", label: "Sök skolor", variant: "primary" },
        { to: "/", label: "Till startsidan", variant: "ghost" },
      ]}
    />
  ),
  notFoundComponent: () => <div className="p-10 text-center">Sidan hittades inte.</div>,
  component: OmdomenOversikt,
});

function OmdomenOversikt() {
  const { data: reviews } = useSuspenseQuery(listOpts);
  const { data: stats } = useSuspenseQuery(statsOpts);
  const [sort, setSort] = useState<"nyast" | "hogst" | "lagst">("nyast");
  const [minBetyg, setMinBetyg] = useState(0);
  const [roll, setRoll] = useState<string>("alla");

  const filtered = useMemo(() => {
    let list = [...reviews];
    if (minBetyg > 0) list = list.filter((r) => r.rating >= minBetyg);
    if (roll !== "alla") list = list.filter((r) => r.role === roll);
    if (sort === "nyast") list.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    if (sort === "hogst") list.sort((a, b) => b.rating - a.rating);
    if (sort === "lagst") list.sort((a, b) => a.rating - b.rating);
    return list;
  }, [reviews, sort, minBetyg, roll]);

  return (
    <section className="mx-auto max-w-6xl px-5 py-section">
      <header className="max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-pill bg-primary-soft px-3 py-1 text-small font-semibold text-primary">
          <ShieldCheck className="h-3.5 w-3.5" /> Modererat och transparent
        </span>
        <h1 className="mt-3 font-display text-h1 leading-tight sm:text-h1 lg:text-display">Omdömesöversikt</h1>
        <p className="mt-3 text-h4 text-ink-muted">
          Vad tycker familjer, elever och personal om Sveriges skolor? Alla omdömen läses av en människa innan publicering.
        </p>
      </header>

      <div className="mt-8 grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="space-y-6 rounded-card border border-border bg-surface p-card shadow-1 lg:sticky lg:top-24 lg:self-start">
          <h2 className="sr-only">Filtrera och sortera omdömen</h2>
          <div>

            <p className="text-small font-semibold uppercase tracking-wider text-ink-muted">Snittbetyg</p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display text-h1">{stats.average.toFixed(1)}</span>
              <span className="text-small text-ink-muted">av 5</span>
            </div>
            <StarRating value={stats.average} />
            <p className="mt-1 text-small text-ink-muted">Baserat på {stats.count} publicerade omdömen</p>
          </div>

          <div className="space-y-1.5">
            {stats.distribution.map((f) => {
              const pct = stats.count ? (f.count / stats.count) * 100 : 0;
              return (
                <button
                  key={f.rating}
                  type="button"
                  onClick={() => setMinBetyg(minBetyg === f.rating ? 0 : f.rating)}
                  className={`flex w-full items-center gap-2 rounded-field px-1.5 py-1 text-small transition ${minBetyg === f.rating ? "bg-primary-soft" : "hover:bg-primary-soft/50"}`}
                >
                  <span className="w-6 text-left font-semibold">{f.rating}★</span>
                  <span className="h-1.5 flex-1 overflow-hidden rounded-pill bg-border">
                    <span className="block h-full rounded-pill bg-accent" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="w-8 text-right tabular-nums text-ink-muted">{f.count}</span>
                </button>
              );
            })}
          </div>

          <div>
            <label htmlFor="omdomen-roll" className="block text-small font-semibold uppercase tracking-wider text-ink-muted">Roll</label>
            <select id="omdomen-roll" value={roll} onChange={(e) => setRoll(e.target.value)} className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-small">
              <option value="alla">Alla</option>
              <option value="vårdnadshavare">Vårdnadshavare</option>
              <option value="elev">Elev</option>
              <option value="tidigare elev">Tidigare elev</option>
              <option value="personal">Personal</option>
            </select>
          </div>

          <div>
            <label htmlFor="omdomen-sortera" className="block text-small font-semibold uppercase tracking-wider text-ink-muted">Sortera</label>
            <select id="omdomen-sortera" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-small">
              <option value="nyast">Nyaste först</option>
              <option value="hogst">Högsta betyg</option>
              <option value="lagst">Lägsta betyg</option>
            </select>
          </div>

          <Link to="/skolor" className="inline-flex w-full items-center justify-center gap-1.5 rounded-card border border-border px-3 py-2.5 text-small font-semibold text-ink hover:bg-primary-soft">
            <PenSquare className="h-4 w-4" /> Skriv ett omdöme
          </Link>
        </aside>

        <div>
          <h2 className="sr-only">Publicerade omdömen</h2>
          {filtered.length === 0 ? (

            <div className="rounded-card border border-dashed border-border bg-surface p-card text-center">
              <p className="font-display text-h3">Inga omdömen matchade dina filter</p>
              <p className="mt-1 text-small text-ink-muted">Prova att ta bort ett filter eller sänk minimibetyget.</p>
              <button type="button" onClick={() => { setMinBetyg(0); setRoll("alla"); }} className="mt-4 rounded-card bg-primary px-4 py-2 text-small font-semibold text-primary-foreground hover:bg-primary-hover">Rensa filter</button>
            </div>
          ) : (
            <div className="grid gap-4">
              <p className="text-small text-ink-muted">Visar {filtered.length} av {reviews.length} publicerade omdömen</p>
              {filtered.map((r) => (
                <div key={r.id} className="space-y-2">
                  <Link to="/skola/$slug" params={{ slug: r.school_slug }} className="text-small font-semibold uppercase tracking-wider text-primary hover:underline">
                    {r.school_name} →
                  </Link>
                  <ReviewCard review={r} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
